import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Calendar, Download as DownloadIcon, FileType } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  DocumentDetailLayout,
  DocumentReviewDetailItem,
} from "@/components/request-detail";
import DocumentAttachments from "@/pages/(home)/_components/DocumentAttachments";
import DocumentQuickPreview from "@/pages/(home)/_components/DocumentQuickPreview";
import { useGetProjectAttachmentByIdQuery } from "@/services/(company)/api.projects";
import type { ISingleProjectAttachment } from "@/types/(company)/projects.interface";
import {
  attachmentIdKey,
  projectIdKey,
} from "@/routes/company/routes.projects.constants";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useLocale } from "@/hooks/useLocale";
import { withBranding } from "@/utils/helper";
import {
  buildFileDownloadUrl,
  buildFilePresignedUrl,
} from "@/utils/file.utils";

export const ProjectAttachmentDetailPage = () => {
  const { t } = useTranslation(["projects", "common"]);
  const { formatDate } = useLocale();
  usePageTitle(withBranding(t("projects:attachment_detail")));
  const navigate = useNavigate();
  const params = useParams();
  const projectId = params[projectIdKey];
  const attachmentId = params[attachmentIdKey];

  const { state } = useLocation() as {
    state: { attachment: ISingleProjectAttachment } | null;
  };

  const { data, isLoading } = useGetProjectAttachmentByIdQuery(
    { projectId: projectId ?? "", attachmentId: attachmentId ?? "" },
    { skip: !projectId || !attachmentId },
  );

  // Use API data when available, fall back to router state for instant display
  const attachment = data?.data ?? state?.attachment;

  if (!projectId || !attachmentId) return null;
  const previewUrl =
    attachment?.fileUrl ||
    (attachment?.fileId ? buildFilePresignedUrl(attachment.fileId) : "");
  const downloadUrl = attachment?.fileId
    ? buildFileDownloadUrl(attachment.fileId, attachment.name)
    : (attachment?.fileUrl ?? "");

  return (
    <DocumentDetailLayout
      title={t("projects:attachment_detail")}
      onBack={() => navigate(-1)}
      isLoading={isLoading && !state?.attachment}
      previewPanel={
        previewUrl ? <DocumentQuickPreview images={[previewUrl]} /> : undefined
      }
      footer={
        attachment ? (
          <>
            <Button variant="outline" onClick={() => navigate(-1)}>
              {t("common:buttons.back")}
            </Button>
            <a
              href={downloadUrl}
              download={attachment.name}
              className="inline-flex items-center justify-center gap-2 h-10 px-4 rounded-sm bg-success-500 text-white hover:bg-success-600 transition-colors text-sm font-medium"
            >
              <DownloadIcon className="w-4 h-4" />
              {t("common:buttons.download")}
            </a>
          </>
        ) : undefined
      }
    >
      {attachment ? (
        <>
          <h5 className="mt-3 mb-8 font-medium">
            {t("projects:attachment_information")}
          </h5>
          <div className="flex flex-col w-full mb-8 gap-7">
            <DocumentReviewDetailItem
              icon={<FileType className="w-5 h-5 text-primary" />}
              color="primary/10"
              label={t("projects:file_name")}
              value={attachment.name}
              valueClassName="font-medium"
            />
            <DocumentReviewDetailItem
              icon={<FileType className="w-5 h-5 text-primary" />}
              color="primary/10"
              label={t("projects:file_type")}
              value={attachment.fileType.toUpperCase()}
              valueClassName="font-medium uppercase"
            />
            <DocumentReviewDetailItem
              icon={<Calendar className="w-5 h-5 text-[#1AD598]" />}
              color="[#1AD5981A]"
              label={t("projects:uploaded_on")}
              value={formatDate(new Date(attachment.createdAt), "dd MMM yyyy")}
              valueClassName="font-medium"
            />
            {attachment.description && (
              <DocumentReviewDetailItem
                icon={<FileType className="w-5 h-5 text-primary" />}
                color="primary/10"
                label={t("projects:description")}
                value={attachment.description}
                valueClassName="font-medium"
              />
            )}
          </div>
          <DocumentAttachments
            items={[{ label: attachment.name, url: previewUrl }]}
          />
        </>
      ) : (
        !isLoading && (
          <p className="text-sm text-muted-foreground mt-6">
            {t("projects:attachment_not_found")}
          </p>
        )
      )}
    </DocumentDetailLayout>
  );
};
